import React, { useState } from 'react';

const EntityDashboard = ({ entities, entityCounts, onSearch, searchResults }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [filterBySearchResults, setFilterBySearchResults] = useState(false);

    const handleSearch = () => {
        if (searchQuery.trim()) {
            onSearch(searchQuery.trim());
            setFilterBySearchResults(true);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setFilterBySearchResults(false);
        onSearch(''); // Clear search results
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const getEntityColor = (category) => {
        const colors = {
            people: 'primary',
            organizations: 'success',
            locations: 'info',
            countries: 'warning',
            threat_actors: 'danger',
            threatActors: 'danger',
            systems: 'secondary',
            mitre_tactics: 'dark',
            mitre_techniques: 'dark',
            mitreTechniques: 'dark',
            cves: 'danger',
            priorities: 'warning',
            cywareIntel: 'primary'
        };
        return colors[category] || 'secondary';
    };

    // Get filtered entities based on search results if filter is active
    const getFilteredEntities = () => {
        let filtered = selectedCategory === 'all' 
            ? entities 
            : { [selectedCategory]: entities[selectedCategory] };
        
        // If we have search results and filter is active, only show matching entities
        if (filterBySearchResults && searchResults && searchResults.length > 0) {
            const searchedEntities = {};
            const searchedItems = new Set(searchResults.map(r => r.entity.toLowerCase()));
            
            Object.entries(filtered).forEach(([category, items]) => {
                if (Array.isArray(items)) {
                    const matchingItems = items.filter(item => {
                        const itemText = typeof item === 'string' ? item : (item.name || item.value || '');
                        return searchedItems.has(itemText.toLowerCase());
                    });
                    if (matchingItems.length > 0) {
                        searchedEntities[category] = matchingItems;
                    }
                }
            });
            
            return searchedEntities;
        }
        
        return filtered;
    };

    const filteredEntities = getFilteredEntities();

    return (
        <div>
            {/* Search Bar */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="fas fa-search"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search entities (e.g., 'APT29', 'CVE-2021-34527', 'phishing')"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                />
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSearch}
                                    disabled={!searchQuery.trim()}
                                >
                                    Search
                                </button>
                                {(searchResults && searchResults.length > 0) && (
                                    <button
                                        className="btn btn-secondary"
                                        onClick={clearSearch}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="all">All Categories</option>
                                {Object.keys(entities).map(category => (
                                    <option key={category} value={category}>
                                        {category.replace(/_/g, ' ').toUpperCase()} ({entityCounts[category] || 0})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Entity Counts Overview */}
            <div className="row mb-4">
                {Object.entries(entityCounts).map(([category, count]) => (
                    <div key={category} className="col-md-3 col-sm-6 mb-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <h3 className={`text-${getEntityColor(category)}`}>{count}</h3>
                                <p className="card-text small mb-0">
                                    {category.replace(/_/g, ' ').toUpperCase()}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Search Results */}
            {searchResults && searchResults.length > 0 && (
                <div className="card mb-4 border-primary">
                    <div className="card-header bg-primary text-white">
                        <h6 className="mb-0">
                            <i className="fas fa-search me-2"></i>
                            Search Results for "{searchQuery}" ({searchResults.length} matches)
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="mb-3">
                            <div className="form-check">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    id="filterByResults"
                                    checked={filterBySearchResults}
                                    onChange={(e) => setFilterBySearchResults(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="filterByResults">
                                    Filter dashboard to show only search results
                                </label>
                            </div>
                        </div>
                        <div className="row">
                            {searchResults.map((result, index) => (
                                <div key={index} className="col-md-6 col-lg-4 mb-2">
                                    <div className="border rounded p-2">
                                        <strong>{result.entity}</strong>
                                        <span className={`badge bg-${getEntityColor(result.category)} ms-2`}>
                                            {result.category.replace(/_/g, ' ')}
                                        </span>
                                        {result.context && (
                                            <p className="mb-0 mt-1 small text-muted">
                                                {result.context}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Entity Details */}
            <div className="row">
                {Object.entries(filteredEntities).map(([category, items]) => {
                    if (!Array.isArray(items) || items.length === 0) return null;
                    
                    return (
                        <div key={category} className="col-md-6 mb-4">
                            <div className="card h-100">
                                <div className="card-header">
                                    <h6 className="mb-0">
                                        <i className="fas fa-tags me-2"></i>
                                        {category.replace(/_/g, ' ').toUpperCase()}
                                        <span className={`badge bg-${getEntityColor(category)} ms-2`}>
                                            {items.length}
                                        </span>
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex flex-wrap gap-2">
                                        {items.slice(0, 20).map((item, index) => {
                                            const displayText = typeof item === 'object' 
                                                ? (item.name || item.value || JSON.stringify(item))
                                                : item;
                                            
                                            return (
                                                <span
                                                    key={index}
                                                    className={`badge bg-${getEntityColor(category)} entity-badge`}
                                                    title={displayText}
                                                >
                                                    {displayText.length > 30 
                                                        ? displayText.substring(0, 30) + '...'
                                                        : displayText
                                                    }
                                                </span>
                                            );
                                        })}
                                        {items.length > 20 && (
                                            <span className="badge bg-light text-dark">
                                                +{items.length - 20} more
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {Object.keys(filteredEntities).length === 0 && (
                <div className="text-center py-5">
                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No entities found</h5>
                    <p className="text-muted">
                        {selectedCategory === 'all' 
                            ? 'No entities were extracted from this document.'
                            : `No entities found in the ${selectedCategory.replace(/_/g, ' ')} category.`
                        }
                    </p>
                </div>
            )}
        </div>
    );
};

export default EntityDashboard;
